import { Fragment, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
  RocketLaunchIcon,
  HandRaisedIcon,
  PlayIcon,
  PlusIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AppBadge, Badge, Btn, Card, cx } from '../../../components/admin/console';
import { useApps, useOtaAudit } from './data/hooks';
import { OTA_RELEASE_CHANNEL_ORDER, channelBadgeTone, channelLabel } from './data/channelLabels';
import type { AuditAction, AuditEntry, OtaAppId, OtaChannelId } from './data/types';

const ACTIONS: AuditAction[] = ['promote', 'halt', 'resume', 'widen-rollout', 'rollback', 'prune-build'];
const ACTORS = ['ci', 'tony', 'sarah', 'super', 'myles'];
const CHANNELS: OtaChannelId[] = [...OTA_RELEASE_CHANNEL_ORDER];

interface FilterState {
  app: OtaAppId | 'all';
  channel: OtaChannelId | 'all';
  actor: string;
  action: AuditAction | 'all';
}

export function OtaAuditPage() {
  const { data: apps = [] } = useApps();
  const { data: audit = [] } = useOtaAudit();
  const [filters, setFilters] = useState<FilterState>({
    app: 'all',
    channel: 'all',
    actor: 'all',
    action: 'all',
  });
  const [expanded, setExpanded] = useState<number | null>(null);

  const rows = useMemo(() => {
    return audit.filter((e) => {
      if (filters.app !== 'all' && e.app !== filters.app) return false;
      if (filters.channel !== 'all' && e.channel !== filters.channel) return false;
      if (filters.actor !== 'all' && e.actor !== filters.actor) return false;
      if (filters.action !== 'all' && e.action !== filters.action) return false;
      return true;
    });
  }, [audit, filters]);

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
            Paper Trail
          </div>
          <h1 className="text-2xl font-bold text-white mt-1">Audit log</h1>
          <p className="text-sm text-slate-400 mt-1">
            Every promote, halt, resume, widen, rollback, and prune. Append-only.
          </p>
        </div>
        <Btn variant="outline" leftIcon={<ArrowDownTrayIcon className="w-3.5 h-3.5" />}>
          Export CSV
        </Btn>
      </div>

      <Card padded={false} className="px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <Select
            label="app"
            value={filters.app}
            onChange={(v) => setFilters((f) => ({ ...f, app: v as FilterState['app'] }))}
            options={[
              ['all', 'all apps'],
              ...apps.map((a) => [a.id, a.name] as [string, string]),
            ]}
          />
          <Select
            label="channel"
            value={filters.channel}
            onChange={(v) => setFilters((f) => ({ ...f, channel: v as FilterState['channel'] }))}
            options={[
              ['all', 'all channels'],
              ...CHANNELS.map((c) => [c, channelLabel(c)] as [string, string]),
            ]}
          />
          <Select
            label="actor"
            value={filters.actor}
            onChange={(v) => setFilters((f) => ({ ...f, actor: v }))}
            options={[
              ['all', 'all actors'],
              ...ACTORS.map((a) => [a, a] as [string, string]),
            ]}
          />
          <Select
            label="action"
            value={filters.action}
            onChange={(v) => setFilters((f) => ({ ...f, action: v as FilterState['action'] }))}
            options={[
              ['all', 'all actions'],
              ...ACTIONS.map((a) => [a, a] as [string, string]),
            ]}
          />
          <div className="flex-1" />
          <span className="text-[11px] text-slate-500 font-mono">{rows.length} entries</span>
        </div>
      </Card>

      <Card padded={false} className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60">
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <th className="w-6 px-2 py-3"></th>
              <th className="text-left px-3 py-3">Timestamp</th>
              <th className="text-left px-2 py-3">Actor</th>
              <th className="text-left px-2 py-3">Action</th>
              <th className="text-left px-2 py-3">App</th>
              <th className="text-left px-2 py-3">Channel</th>
              <th className="text-left px-2 py-3">From → To</th>
              <th className="text-left px-2 py-3">Smoke</th>
              <th className="text-left px-3 py-3">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e, i) => (
              <Fragment key={i}>
                <tr
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="border-t border-slate-700/40 hover:bg-slate-700/20 cursor-pointer density-row"
                >
                  <td className="px-2 py-2.5 text-slate-500">
                    <ChevronRightIcon
                      className={cx(
                        'w-3 h-3 transition-transform',
                        expanded === i && 'rotate-90',
                      )}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-[11px] font-mono text-slate-400">
                    {e.t.replace('T', ' ').replace('Z', ' UTC')}
                  </td>
                  <td className="px-2 py-2.5">
                    <span className="font-mono text-[11px] text-slate-300">{e.actor}</span>
                  </td>
                  <td className="px-2 py-2.5">
                    <ActionBadge action={e.action} />
                  </td>
                  <td className="px-2 py-2.5">
                    <AppBadge app={e.app} size="sm" />
                  </td>
                  <td className="px-2 py-2.5">
                    {e.channel && (
                      <Badge tone={channelBadgeTone(e.channel)}>{channelLabel(e.channel)}</Badge>
                    )}
                  </td>
                  <td className="px-2 py-2.5 font-mono text-[11px]">
                    {e.from != null ? (
                      <>
                        <span className="text-slate-400">{e.from}</span>{' '}
                        <span className="text-slate-600">→</span>{' '}
                        <span className="text-white font-bold">{e.to ?? '∅'}</span>
                      </>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    {e.smoke === 'pass' && (
                      <Badge tone="success" dot>
                        pass
                      </Badge>
                    )}
                    {e.smoke === 'fail' && (
                      <Badge tone="error" dot>
                        fail
                      </Badge>
                    )}
                    {!e.smoke && <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-slate-300 max-w-[320px] truncate">
                    {e.reason}
                  </td>
                </tr>
                {expanded === i && <ExpandedRow entry={e} />}
              </Fragment>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-slate-500">
            No audit entries match the current filter.
          </div>
        )}
      </Card>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs text-white font-mono"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

const ACTION_TONE: Record<AuditAction, 'info' | 'error' | 'success' | 'warning' | 'neutral'> = {
  promote: 'info',
  halt: 'error',
  resume: 'success',
  'widen-rollout': 'warning',
  rollback: 'error',
  'prune-build': 'neutral',
};
const ACTION_ICON: Record<AuditAction, React.ReactNode> = {
  promote: <RocketLaunchIcon className="w-3 h-3" />,
  halt: <HandRaisedIcon className="w-3 h-3" />,
  resume: <PlayIcon className="w-3 h-3" />,
  'widen-rollout': <PlusIcon className="w-3 h-3" />,
  rollback: <ArrowUturnLeftIcon className="w-3 h-3" />,
  'prune-build': <XMarkIcon className="w-3 h-3" />,
};

function ActionBadge({ action }: { action: AuditAction }) {
  return (
    <Badge tone={ACTION_TONE[action]}>
      {ACTION_ICON[action]}
      {action}
    </Badge>
  );
}

function ExpandedRow({ entry }: { entry: AuditEntry }) {
  const payload = {
    manifest_url: `https://ota.vortexmarine.co.nz/${entry.channel ?? '—'}/${entry.app}/manifest.json`,
    served_versionCode: entry.to,
    sha256_match: true,
    min_required_version_ok: true,
    response_time_ms: 87 + ((entry.t.charCodeAt(15) ?? 0) % 40),
    checked_at: entry.t,
    actor: entry.actor,
  };
  return (
    <tr className="bg-slate-900/60 border-t border-slate-700/40">
      <td colSpan={9} className="px-8 py-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
          Smoke check payload
        </div>
        <pre className="text-[11px] font-mono text-slate-300 bg-slate-950/60 border border-slate-700 rounded-lg p-3 overflow-x-auto">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </td>
    </tr>
  );
}

export default OtaAuditPage;
